import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, X, Building2, User, Calendar, TrendingUp, Megaphone, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMarketingLeads, useBranches, useTeamMembers, useCustomers } from '../hooks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pagination options
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const LEAD_SOURCES = [
  'Facebook',
  'Instagram', 
  'Google Ads',
  'Website',
  'Email Campaign',
  'WhatsApp',
  'Referral',
  'Trade Show',
  'Field Marketing',
  'Other'
];

export const MarketingLeadsList = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('all');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  const customerInputRef = useRef(null);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const { leads, loading, refetch: refetchLeads } = useMarketingLeads();
  const { branches } = useBranches();
  const { teamMembers, getFilteredByBranch } = useTeamMembers();
  const { searchCustomers } = useCustomers();
  
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    campaign_name: '',
    source: 'Website',
    product_interest: '',
    estimated_value: '',
    priority: 'medium',
    status: 'new',
    follow_up_date: '',
    notes: ''
  });

  // Initialize filters from URL params
  useEffect(() => {
    const page = searchParams.get('page');
    const branch = searchParams.get('branch');
    const source = searchParams.get('source');
    const status = searchParams.get('status');
    const assignedTo = searchParams.get('assignedTo');
    const query = searchParams.get('q');
    
    if (page) setCurrentPage(parseInt(page, 10));
    if (branch) setBranchFilter(branch);
    if (source) setSourceFilter(source);
    if (status) setStatusFilter(status);
    if (assignedTo) setAssignedToFilter(assignedTo);
    if (query) setSearchQuery(query);
    
    const savedScroll = sessionStorage.getItem('marketingLeadsListScroll');
    if (savedScroll) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScroll, 10));
        sessionStorage.removeItem('marketingLeadsListScroll');
      }, 100);
    }
  }, []);

  // Sync filters to URL
  const syncFiltersToUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (branchFilter !== 'all') params.set('branch', branchFilter);
    if (sourceFilter !== 'all') params.set('source', sourceFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (assignedToFilter !== 'all') params.set('assignedTo', assignedToFilter);
    if (searchQuery) params.set('q', searchQuery);
    setSearchParams(params, { replace: true });
  }, [currentPage, branchFilter, sourceFilter, statusFilter, assignedToFilter, searchQuery, setSearchParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      syncFiltersToUrl();
    }, 300);
    return () => clearTimeout(timer);
  }, [syncFiltersToUrl]);

  // Save scroll position before navigating to detail
  const handleLeadClick = () => {
    sessionStorage.setItem('marketingLeadsListScroll', window.scrollY.toString());
  };

  useEffect(() => {
    filterLeads();
  }, [searchQuery, branchFilter, sourceFilter, statusFilter, assignedToFilter, leads]);

  const filterLeads = () => {
    let filtered = [...leads];
    
    if (branchFilter && branchFilter !== 'all') {
      filtered = filtered.filter(lead => lead.branch_id === branchFilter);
    }
    
    if (sourceFilter && sourceFilter !== 'all') {
      filtered = filtered.filter(lead => lead.source === sourceFilter);
    }
    
    if (statusFilter && statusFilter !== 'all') {
      if (statusFilter === 'active') {
        // Active means not won, lost, or disqualified
        filtered = filtered.filter(lead => !['won', 'lost', 'disqualified'].includes(lead.status));
      } else {
        filtered = filtered.filter(lead => lead.status === statusFilter);
      }
    }
    
    if (assignedToFilter && assignedToFilter !== 'all') {
      if (assignedToFilter === 'unassigned') {
        filtered = filtered.filter(lead => !lead.assigned_to);
      } else {
        filtered = filtered.filter(lead => lead.assigned_to === assignedToFilter);
      }
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(lead =>
        lead.customer_name?.toLowerCase().includes(query) ||
        lead.campaign_name?.toLowerCase().includes(query) ||
        lead.product_interest?.toLowerCase().includes(query) ||
        lead.customer_phone?.toLowerCase().includes(query)
      );
    }
    
    setFilteredLeads(filtered);
    // Reset to page 1 when filters change
    if (currentPage > Math.ceil(filtered.length / pageSize)) {
      setCurrentPage(1);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredLeads.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedLeads = filteredLeads.slice(startIndex, endIndex);

  const clearFilters = () => {
    setSearchQuery('');
    setBranchFilter('all');
    setSourceFilter('all');
    setStatusFilter('all');
    setAssignedToFilter('all');
    setCurrentPage(1);
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || branchFilter !== 'all' || sourceFilter !== 'all' || statusFilter !== 'all' || assignedToFilter !== 'all';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (submitting) return;
    
    setSubmitting(true);
    try {
      const submitData = {
        customer_id: formData.customer_id,
        campaign_name: formData.campaign_name,
        source: formData.source,
        product_interest: formData.product_interest,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
        priority: formData.priority,
        status: formData.status,
        follow_up_date: formData.follow_up_date || null,
        notes: formData.notes || null
      };
      await axios.post(`${API}/marketing-leads`, submitData);
      toast.success('Marketing lead created successfully');
      setDialogOpen(false);
      setFormData({
        customer_id: '',
        customer_name: '',
        campaign_name: '',
        source: 'Website',
        product_interest: '',
        estimated_value: '',
        priority: 'medium',
        status: 'new',
        follow_up_date: '',
        notes: ''
      });
      setCustomerSearch('');
      refetchLeads();
    } catch (error) {
      toast.error('Failed to create marketing lead');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCustomers = searchCustomers(customerSearch);

  const handleCustomerSelect = (customer) => {
    setFormData({ ...formData, customer_id: customer.id, customer_name: customer.name });
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    setShowCustomerDropdown(value.length >= 3);
    if (value.length < 3) {
      setFormData({ ...formData, customer_id: '', customer_name: '' });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      qualified: 'bg-purple-100 text-purple-800',
      won: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-slate-100 text-slate-800';
  };

  // Use hook's filter function for team members by branch
  const filteredTeamMembers = getFilteredByBranch(branchFilter);

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      <Navbar setSidebarOpen={setSidebarOpen} />
      
      <main className="md:ml-64 pt-16">
        <div className="p-6 md:p-8" data-testid="marketing-leads-page">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Megaphone className="h-6 w-6 text-purple-600" />
                Marketing Leads
              </h1>
              <p className="text-slate-500 mt-1">Track and manage leads from marketing campaigns</p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/marketing-reports">
                <Button variant="outline" data-testid="marketing-reports-button">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  View Reports
                </Button>
              </Link>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-marketing-lead-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Lead
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Marketing Lead</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="customer">Customer Name *</Label>
                      <p className="text-xs text-slate-500">Type at least 3 characters to search</p>
                      <div className="relative" ref={customerInputRef}>
                        <Input
                          id="customer"
                          value={customerSearch}
                          onChange={handleCustomerSearchChange}
                          placeholder="Search customer..."
                          autoComplete="off"
                          data-testid="marketing-lead-customer-input"
                        />
                        {showCustomerDropdown && filteredCustomers.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                            {filteredCustomers.map((customer) => (
                              <div
                                key={customer.id}
                                className="px-4 py-2 hover:bg-slate-100 cursor-pointer"
                                onClick={() => handleCustomerSelect(customer)}
                              >
                                <p className="font-medium">{customer.name}</p>
                                <p className="text-sm text-slate-500">{customer.phone}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="campaign_name">Campaign Name *</Label>
                      <Input
                        id="campaign_name"
                        value={formData.campaign_name}
                        onChange={(e) => setFormData({ ...formData, campaign_name: e.target.value })}
                        placeholder="e.g., Summer Sale 2026"
                        required
                        data-testid="marketing-lead-campaign-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="source">Lead Source *</Label>
                        <Select value={formData.source} onValueChange={(value) => setFormData({ ...formData, source: value })}>
                          <SelectTrigger data-testid="marketing-lead-source-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LEAD_SOURCES.map((source) => (
                              <SelectItem key={source} value={source}>{source}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="priority">Priority</Label>
                        <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                          <SelectTrigger data-testid="marketing-lead-priority-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="product_interest">Product Interest *</Label>
                      <Input
                        id="product_interest"
                        value={formData.product_interest}
                        onChange={(e) => setFormData({ ...formData, product_interest: e.target.value })}
                        placeholder="e.g., Kitchen Appliances"
                        required
                        data-testid="marketing-lead-product-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="estimated_value">Estimated Value (₹)</Label>
                        <Input
                          id="estimated_value"
                          type="number"
                          value={formData.estimated_value}
                          onChange={(e) => setFormData({ ...formData, estimated_value: e.target.value })}
                          placeholder="0"
                          data-testid="marketing-lead-value-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="follow_up_date">Follow Up Date</Label>
                        <Input
                          id="follow_up_date"
                          type="date"
                          value={formData.follow_up_date}
                          onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
                          data-testid="marketing-lead-followup-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        placeholder="Additional notes..."
                        data-testid="marketing-lead-notes-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" size="lg" disabled={submitting} data-testid="submit-marketing-lead-button">
                      {submitting ? 'Saving...' : 'Create Lead'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by customer, campaign, or product..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                  data-testid="marketing-lead-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {branches.length > 0 && (
                <Select value={branchFilter} onValueChange={setBranchFilter}>
                  <SelectTrigger className="w-40" data-testid="marketing-lead-branch-filter">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Branch" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-40" data-testid="marketing-lead-source-filter">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {LEAD_SOURCES.map((source) => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="marketing-lead-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Assigned To Filter */}
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-slate-500" />
                <Select value={assignedToFilter} onValueChange={setAssignedToFilter}>
                  <SelectTrigger className="w-40" data-testid="marketing-lead-assigned-to-filter">
                    <SelectValue placeholder="Assigned To" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Assignees</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {filteredTeamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {hasActiveFilters && (
              <div className="flex items-center gap-4 mt-2">
                <p className="text-sm text-slate-500">
                  Found {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''}
                </p>
                <button onClick={clearFilters} className="text-sm text-accent hover:underline">
                  Clear filters
                </button>
              </div>
            )}
          </div>

          {/* Pagination Controls - Top */}
          {filteredLeads.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Show</span>
                <Select value={pageSize.toString()} onValueChange={(val) => { setPageSize(parseInt(val)); setCurrentPage(1); }}>
                  <SelectTrigger className="w-20" data-testid="marketing-lead-page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-slate-600">per page</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredLeads.length)} of {filteredLeads.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Leads Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <Megaphone className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">
                {hasActiveFilters ? 'No marketing leads match your filters.' : 'No marketing leads yet. Create your first lead to get started.'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Campaign</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Source</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Value</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Follow Up</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {paginatedLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-slate-50 transition-colors" data-testid={`marketing-lead-row-${lead.id}`}>
                        <td className="px-6 py-4">
                          <Link to={`/marketing-leads/${lead.id}`} onClick={handleLeadClick} className="text-sm font-semibold text-purple-600 hover:text-purple-800 hover:underline">
                            {lead.customer_name}
                          </Link>
                          {lead.customer_phone && (
                            <p className="text-xs text-slate-500">{lead.customer_phone}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-900">{lead.campaign_name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{lead.source}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{lead.product_interest}</td>
                        <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                          {lead.estimated_value ? `₹${lead.estimated_value.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(lead.status)}`}>
                            {lead.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {lead.follow_up_date ? (
                            <span className="flex items-center gap-1 text-orange-600">
                              <Calendar className="h-3 w-3" />
                              {new Date(lead.follow_up_date).toLocaleDateString()}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination Controls - Bottom */}
          {filteredLeads.length > pageSize && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="flex items-center px-4 text-sm text-slate-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
